import { Star } from 'lucide-react';
import { useStore } from '../../store/useStore';
import { weeklyInsightsEnabled } from '../../native/insightsGate';

/** Focus works best at 3–5 ★ (Chad, 18 Oct 2026 — was "core" at 3–7; soft guidance, no hard limit). */
export const CORE_SOFT_MAX = 5;
export const CORE_TIP_MIN_TAGS = 3;

export const CORE_COPY = {
  tip: 'Star the 3–5 projects or areas you’re focusing on right now — your weekly review shows whether your week went there.',
  tooMany: 'Focus works best at 3–5 stars — with more, “focus” stops meaning much.',
  on: 'Focus — I’m focusing on this right now (tap to unstar)',
  off: 'Mark as a focus project',
} as const;

/**
 * ★ toggle for one tag (Weekly Insights feature 3). Sits beside the tag
 * wherever tags are managed. Web only: renders nothing when the gate is
 * closed, so native tag pickers are unchanged.
 */
export function CoreStar({ tagId, size = 12 }: { tagId: string; size?: number }) {
  const tag = useStore((s) => s.tags[tagId]);
  const setTagCore = useStore((s) => s.setTagCore);
  if (!weeklyInsightsEnabled() || !tag) return null;
  const on = Boolean(tag.core);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setTagCore(tagId, !on);
      }}
      aria-pressed={on}
      aria-label={`${on ? 'Unstar' : 'Star'} ${tag.name} as focus`}
      title={on ? CORE_COPY.on : CORE_COPY.off}
      className={`inline-flex shrink-0 items-center justify-center rounded-full p-1 transition-colors ${
        on ? 'text-amber-500 hover:text-amber-600' : 'text-slate-300 dark:text-slate-600 hover:text-amber-400'
      }`}
    >
      <Star size={size} fill={on ? 'currentColor' : 'none'} />
    </button>
  );
}

/**
 * The one-time ★ tip (≥3 tags, none starred) and the soft >7 note. Shown
 * under the tag picker; the tip's dismissal is device-local and never
 * repeats. Web only.
 */
export function CoreGuidance() {
  const tags = useStore((s) => s.tags);
  const dismissed = useStore((s) => s.coreTipDismissed);
  const dismiss = useStore((s) => s.dismissCoreTip);
  if (!weeklyInsightsEnabled()) return null;
  const all = Object.values(tags);
  const starred = all.filter((t) => t.core).length;
  if (starred > CORE_SOFT_MAX) {
    return <p className="text-[10px] text-amber-600 dark:text-amber-400">{CORE_COPY.tooMany}</p>;
  }
  if (!dismissed && starred === 0 && all.length >= CORE_TIP_MIN_TAGS) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-900/20 px-2 py-1.5">
        <Star size={12} className="mt-0.5 shrink-0 text-amber-500" />
        <p className="flex-1 text-[10px] leading-relaxed text-amber-800 dark:text-amber-200">{CORE_COPY.tip}</p>
        <button
          type="button"
          onClick={dismiss}
          className="text-[10px] font-bold text-amber-700 dark:text-amber-300 hover:underline"
        >
          Got it
        </button>
      </div>
    );
  }
  return null;
}
